'use client';
import Guard from '@/components/common/Guard';
import Loading from '@/components/common/Loading';
import {
  automaticReloginRequestedAtom,
  claimAutomaticRelogin,
} from '@/components/common/authentication/automaticRelogin';
import useLogout from '@/components/common/authentication/useLogout';
import useSessionNotices, { EXPIRED, UNREACHABLE } from '@/components/common/authentication/useSessionNotices';
import { SocketIOProvider } from '@/components/socketio/SocketIOProvider';
import { apiSdkAtom } from '@/context/atoms/ApiSdk';
import { authMechanismAtom } from '@/context/atoms/AuthMechanism';
import { removeLegacySessionKeys, sessionStateAtom } from '@/context/atoms/Session';
import { userPermissionsAtom } from '@/context/atoms/UserPermissions';
import { useAppModules } from '@/context/modules';
import { WebEamAuthMechanism } from '@/helper/auth/WebEamAuthMechanism';
import { ApiSdkEvents } from '@ekkogmbh/apisdk';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useRouter, useSelectedLayoutSegments } from 'next/navigation';
import React, { type PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';

export type PropsWithLoginSlot = { login: React.ReactNode } & PropsWithChildren;

/**
 * Hands the session to `@ekkogmbh/apisdk` and shows the login page or the app
 * for it. It mirrors every state the SDK publishes into `sessionStateAtom`,
 * which the rest of the app reads.
 */
export default function AuthGuard({ login, children }: PropsWithLoginSlot): React.JSX.Element {
  const api = useAtomValue(apiSdkAtom);
  const authMechanism = useAtomValue(authMechanismAtom);
  const [session, setSession] = useAtom(sessionStateAtom);
  const setReloginRequested = useSetAtom(automaticReloginRequestedAtom);
  const permissions = useAtomValue(userPermissionsAtom);
  const router = useRouter();
  const segments = useSelectedLayoutSegments();
  const [hasPermissions, setHasPermissions] = useState(false);
  const [isStarted, setStarted] = useState(false);
  const startedRef = useRef(false);
  const { getAccessibleModules, findModuleByRouteSegments } = useAppModules();
  const { notifyError, closeErrorNotice } = useSessionNotices();
  const logOut = useLogout();

  // =========================================================
  // Following the session
  // =========================================================

  useEffect(() => {
    setSession(api.session.getState());

    return api.session.subscribe((state, reason) => {
      setSession(state);

      if (reason === 'expired') {
        notifyError(EXPIRED);
      }
    });
  }, [api, setSession, notifyError]);

  useEffect(() => {
    if (session.isAuthenticated) {
      closeErrorNotice();
    }
  }, [session.isAuthenticated, closeErrorNotice]);

  // =========================================================
  // Startup, once per page
  // =========================================================

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    removeLegacySessionKeys();

    if (authMechanism instanceof WebEamAuthMechanism) {
      // WebEAM sessions are not restored: every page load runs the SSO flow
      // again, which the login page starts, and WebEAM answers from its own
      // session without asking the user while that is valid.
      //
      // TODO: Restoring would save the round trip to WebEAM, as the SDK stores
      // WebEAM sessions like JWT ones. The cost is the logout: the E2E library
      // revokes the token it acquired itself (`E2E.token.access_token`), and in
      // a tab that restored, the library holds none, so its logout would revoke
      // nothing at WebEAM. Restoring needs a way to hand the stored token back
      // to the library, which `public/auth/e2e.js` does not offer. The library
      // should be replaced by, or modernised into, a maintained OAuth2/OIDC
      // client that can do that.
      setStarted(true);
      return;
    }

    api.session.restore().then(
      () => setStarted(true),
      () => {
        // The stored session stays, so a reload tries again.
        // TODO EKKO-2140: Show a screen that explains the situation and
        // offers a retry, instead of the login page.
        notifyError(UNREACHABLE);
        setStarted(true);
      },
    );
  }, [api, authMechanism, notifyError]);

  // =========================================================
  // WebEAM: a 403 ends the session
  // =========================================================

  // The backend answers a token the identity provider no longer accepts with
  // 403, which the SDK does not count as an ended session. WebEAM usually still
  // holds its own session, so the login page signs in again on its own, at
  // most once a minute (see `automaticRelogin.ts`); otherwise the user does.
  useEffect(() => {
    if (!(authMechanism instanceof WebEamAuthMechanism)) {
      return;
    }

    // A request that got no answer at all is reported without a response.
    const handleRequestError = (_endpoint: unknown, _statusCode?: unknown, response?: Response): void => {
      // Requests in flight when the token expired all fail; one logout does.
      if (response?.status === 403 && api.session.getState().isAuthenticated) {
        if (claimAutomaticRelogin()) {
          setReloginRequested(true);
        } else {
          notifyError(EXPIRED);
        }
        void logOut();
      }
    };

    api.on(ApiSdkEvents.REQUEST_ERROR, handleRequestError);

    return () => {
      api.off(ApiSdkEvents.REQUEST_ERROR, handleRequestError);
    };
  }, [api, authMechanism, notifyError, logOut, setReloginRequested]);

  // =========================================================
  // Module permissions of the route
  // =========================================================

  // @TODO should be refactored. or find a better way.
  const checkPermissions = useCallback(
    (segments: string[]): boolean => {
      if (segments.length > 0) {
        const Module = findModuleByRouteSegments(segments);
        if (Module !== undefined) {
          return getAccessibleModules(permissions).includes(Module);
        }
      }

      return true;
    },
    [findModuleByRouteSegments, getAccessibleModules, permissions],
  );

  // Before the session is known the user has no permissions, which must not
  // move a page load away from the module it was meant for.
  useEffect(() => {
    if (!session.isAuthenticated) {
      return;
    }

    if (!checkPermissions(segments)) {
      setHasPermissions(false);
      router.push('/');
    } else {
      setHasPermissions(true);
    }
  }, [session.isAuthenticated, checkPermissions, router, segments]);

  if (!isStarted) {
    return <Loading size={150} />;
  }

  if (!session.isAuthenticated) {
    return <>{login}</>;
  }

  return (
    <SocketIOProvider>
      <Guard condition={hasPermissions} fallback={<Loading />}>
        {children}
      </Guard>
    </SocketIOProvider>
  );
}
